import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import {
  selectMetadataMappingStatus,
  selectMetadataMappingError,
  setMetadataMappingLoading,
  setMetadataMappingError,
  selectMetadataMappingMessages,
  addChatMessage,
} from '../../../../reducers/metadataMappingReducer';
import { useAiAgentApiFacade } from '../../../../apis/facades/useAiAgentApiFacade';
import { AiChatMessage } from '../../../common/ai-chat/aiChatTypes';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import { DateTime } from 'luxon';
import { telemetry } from '../../../../telemetry';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../../../services/events';
import { useFunnelMapApiFacade } from '../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { setFunnelMap } from '../../../../reducers/united-canvas/unitedCanvasReducer';
import { normalizeFunnelMapPositions } from '../../funnel-map-flow/normalizeFunnelMapPositions';
import { useAutoSaveContext } from '../../funnel-map-flow/auto-save/AutoSaveContext';
import { setIsPageLoading } from '../../../../reducers/canvasPageStateReducer';
import useUserInfo from '../../../../hooks/useUserInfo';

/**
 * IMPORTANT: This hook should NOT contain any useEffect hooks.
 * It is used in multiple places to retrieve data and provide callback functions.
 * Any side effects (like initializing messages array or calling "complete") should be handled
 * in the consuming components, not in this hook.
 */
export const useMetadataMapping = (funnelMapId: string) => {
  const dispatch = useDispatch();
  const { post_processMining } = useAiAgentApiFacade();
  const { get_funnelMap } = useFunnelMapApiFacade();
  const { funnelMapVersion } = useAutoSaveContext();
  const sendBiEvent = useSendBiEvent();
  const { updateHubspotContact } = useUserInfo();

  const status = useSelector(selectMetadataMappingStatus(funnelMapId));
  const error = useSelector(selectMetadataMappingError(funnelMapId));
  const messages = useSelector(selectMetadataMappingMessages(funnelMapId));
  const isLoading = status === 'loading' || status === 'completedFetchingData';

  const complete = useCallback(async () => {
    if (!funnelMapId) return;

    try {
      const funnelMap = (await get_funnelMap(funnelMapId, true)) as FunnelMap;
      funnelMapVersion.current = funnelMap.version;
      dispatch(setFunnelMap(normalizeFunnelMapPositions(funnelMap)));
      dispatch(setIsPageLoading({ isPageLoading: false }));
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      dispatch(setMetadataMappingLoading({ funnelMapId, isLoading: false }));
    }
  }, [dispatch, funnelMapId, get_funnelMap, funnelMapVersion]);

  const sendMessage = useCallback(
    async (messageContent: string) => {
      if (!funnelMapId) {
        return;
      }
      sendBiEvent({ name: AI_AGENTS_EVENTS.generateProcessMiningFromCanvas });
      updateHubspotContact({ last_ai_chat_date: Date.now() });

      const message: AiChatMessage = {
        role: ChatCompletionRequestMessageRole.USER,
        content: messageContent,
        createdAt: DateTime.now().toISO(),
      };

      dispatch(
        addChatMessage({
          funnelMapId,
          message,
        }),
      );

      try {
        dispatch(setMetadataMappingLoading({ funnelMapId, isLoading: true }));
        await post_processMining(funnelMapId, { message });
      } catch (error) {
        telemetry.captureError(error);
        dispatch(
          setMetadataMappingError({
            funnelMapId,
            error: `Couldn't generate results. Try again with a new prompt.`,
          }),
        );
      }
    },
    [funnelMapId, sendBiEvent, updateHubspotContact, dispatch, post_processMining],
  );

  const latestAssistantMessage = useMemo(() => {
    const assistantMessages = messages.filter(
      (m) => m.role === ChatCompletionRequestMessageRole.ASSISTANT,
    );
    const lastMessage = assistantMessages[assistantMessages.length - 1];
    if (!lastMessage) return null;
    return typeof lastMessage.content === 'string' ? lastMessage.content : null;
  }, [messages]);

  const canSendMessage = status === 'idle' || status === 'error';

  const reset = useCallback(() => {
    dispatch(setMetadataMappingLoading({ funnelMapId, isLoading: false }));
  }, [dispatch, funnelMapId]);

  return {
    messages,
    isLoading,
    error,
    canSendMessage,
    sendMessage,
    status,
    latestAssistantMessage,
    complete,
    reset,
  };
};
